import React, { useEffect, useState } from 'react';
import { Calendar as CalendarIcon, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Booking } from '../../types/booking';
interface DateSelectionProps {
  startDate: string;
  endDate: string;
  onSelect: (start: string, end: string) => void;
  existingBookings?: Booking[];
}
export function DateSelection({
  startDate,
  endDate,
  onSelect,
  existingBookings = []
}: DateSelectionProps) {
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [selectingStart, setSelectingStart] = useState(true);
  const [error, setError] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // Sync local state with props
  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  }, [startDate, endDate]);
  // Convert DD/MM/YYYY to YYYY-MM-DD
  const convertToYYYYMMDD = (dateStr: string): string => {
    if (!dateStr || !dateStr.includes('/')) return dateStr;
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };
  // Check if dates conflict with existing bookings
  const checkDateConflict = (start: string, end: string): boolean => {
    if (existingBookings.length === 0) return false;
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    return existingBookings.some(booking => {
      // Skip completed bookings
      if (booking.status === 'completed') return false;
      // Convert booking dates from DD/MM/YYYY to YYYY-MM-DD
      const bookingStart = convertToYYYYMMDD(booking.startDate);
      const bookingEnd = convertToYYYYMMDD(booking.endDate);
      const bookingStartTime = new Date(bookingStart).getTime();
      const bookingEndTime = new Date(bookingEnd).getTime();
      // Check for overlap
      return startTime <= bookingEndTime && endTime >= bookingStartTime;
    });
  };
  const handleDateChange = (start: string, end: string) => {
    setLocalStartDate(start);
    setLocalEndDate(end);
    if (start && end) {
      const startTime = new Date(start).getTime();
      const endTime = new Date(end).getTime();
      const daysDiff = Math.ceil((endTime - startTime) / (1000 * 60 * 60 * 24));
      // Check 3-month advance booking limit
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxAdvanceDate = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate());
      if (new Date(start) > maxAdvanceDate || new Date(end) > maxAdvanceDate) {
        setError('Bookings cannot be made more than 3 months in advance');
        return;
      }
      if (daysDiff > 14) {
        setError('Booking period cannot exceed 14 days');
        return;
      } else if (daysDiff < 1) {
        setError('End date must be after start date');
        return;
      }
      // Check for conflicts with existing bookings
      if (checkDateConflict(start, end)) {
        setError('These dates conflict with an existing booking. Please select different dates.');
        return;
      }
      setError('');
      onSelect(start, end);
    }
  };
  const handleCalendarDateClick = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    // Check if date is booked
    if (isDateBooked(date)) {
      return;
    }
    if (selectingStart || !localStartDate) {
      // Selecting start date
      setLocalStartDate(dateStr);
      setSelectingStart(false);
      // If we already have an end date, validate the range
      if (localEndDate) {
        handleDateChange(dateStr, localEndDate);
      }
    } else {
      // Selecting end date
      const startTime = new Date(localStartDate).getTime();
      const endTime = date.getTime();
      if (endTime < startTime) {
        // If clicked date is before start, make it the new start
        setLocalStartDate(dateStr);
        setLocalEndDate('');
        setSelectingStart(false);
      } else {
        setLocalEndDate(dateStr);
        handleDateChange(localStartDate, dateStr);
        setSelectingStart(true); // Reset for next selection
      }
    }
  };
  const isDateBooked = (date: Date): boolean => {
    if (existingBookings.length === 0) return false;
    const dateStr = date.toISOString().split('T')[0];
    return existingBookings.some(booking => {
      // Skip completed bookings
      if (booking.status === 'completed') return false;
      // Convert booking dates from DD/MM/YYYY to YYYY-MM-DD
      const bookingStart = convertToYYYYMMDD(booking.startDate);
      const bookingEnd = convertToYYYYMMDD(booking.endDate);
      return dateStr >= bookingStart && dateStr <= bookingEnd;
    });
  };
  const isDateInRange = (date: Date) => {
    if (!localStartDate || !localEndDate) return false;
    const dateStr = date.toISOString().split('T')[0];
    return dateStr >= localStartDate && dateStr <= localEndDate;
  };
  const isDateSelected = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return dateStr === localStartDate || dateStr === localEndDate;
  };
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days = [];
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push(date);
    }
    return days;
  };
  const changeMonth = (offset: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1));
  };
  const formatMonthYear = () => {
    return currentMonth.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };
  return <div className="space-y-6" data-id="element-520">
      {/* Calendar View */}
      <div data-id="element-521">
        <div className="flex items-center justify-between mb-4" data-id="element-522">
          <h3 className="text-sm font-semibold text-slate-700" data-id="element-523">
            {selectingStart && !localStartDate ? 'Select Start Date' : !localEndDate ? 'Select End Date' : 'Availability Calendar'}
          </h3>
          <div className="flex items-center gap-2" data-id="element-524">
            <button onClick={() => changeMonth(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-525">
              <ChevronLeft size={18} className="text-slate-600" data-id="element-526" />
            </button>
            <span className="text-sm font-medium text-slate-700 min-w-[140px] text-center" data-id="element-527">
              {formatMonthYear()}
            </span>
            <button onClick={() => changeMonth(1)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-528">
              <ChevronRight size={18} className="text-slate-600" data-id="element-529" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2" data-id="element-530">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} className="text-slate-500 font-medium py-2" data-id="element-531">
              {d}
            </div>)}
        </div>

        <div className="grid grid-cols-7 gap-1" data-id="element-532">
          {getDaysInMonth().map((date, idx) => {
          if (!date) {
            return <div key={`empty-${idx}`} className="aspect-square" data-id="element-533" />;
          }
          const isBooked = isDateBooked(date);
          const isSelected = isDateSelected(date);
          const inRange = isDateInRange(date);
          const dateStr = date.toISOString().split('T')[0];
          const isToday = dateStr === new Date().toISOString().split('T')[0];
          const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const maxAdvanceDate = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate());
          const isTooFarAhead = date > maxAdvanceDate;
          return <button key={idx} onClick={() => handleCalendarDateClick(date)} disabled={isBooked || isPast || isTooFarAhead} className={`
                  aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all duration-200
                  ${isBooked || isPast ? 'bg-red-100 text-red-400 cursor-not-allowed' : isTooFarAhead ? 'bg-slate-100 text-slate-300 cursor-not-allowed' : isSelected ? 'bg-slate-900 text-white shadow-md scale-105' : inRange ? 'bg-slate-200 text-slate-700' : 'bg-slate-50 text-slate-700 hover:bg-slate-100 hover:scale-105'}
                  ${isToday && !isSelected ? 'ring-2 ring-slate-400' : ''}
                `} data-id="element-534">
                {date.getDate()}
              </button>;
        })}
        </div>

        <div className="flex items-center gap-4 mt-3 text-xs" data-id="element-535">
          <div className="flex items-center gap-1.5" data-id="element-536">
            <div className="w-3 h-3 bg-slate-900 rounded" data-id="element-537"></div>
            <span className="text-slate-600" data-id="element-538">Selected</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-539">
            <div className="w-3 h-3 bg-red-100 rounded" data-id="element-540"></div>
            <span className="text-slate-600" data-id="element-541">Unavailable</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-542">
            <div className="w-3 h-3 bg-slate-50 rounded border border-slate-200" data-id="element-543"></div>
            <span className="text-slate-600" data-id="element-544">Available</span>
          </div>
        </div>
      </div>

      {/* Date Inputs */}
      <div className="grid grid-cols-2 gap-4" data-id="element-545">
        <div data-id="element-546">
          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-547">
            Start Date
          </label>
          <input type="date" value={localStartDate} onChange={e => {
          setLocalStartDate(e.target.value);
          if (e.target.value && localEndDate) {
            handleDateChange(e.target.value, localEndDate);
          }
        }} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent text-slate-900 text-sm" data-id="element-548" />
        </div>

        <div data-id="element-549">
          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-550">
            End Date
          </label>
          <input type="date" value={localEndDate} onChange={e => {
          setLocalEndDate(e.target.value);
          if (localStartDate && e.target.value) {
            handleDateChange(localStartDate, e.target.value);
          }
        }} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent text-slate-900 text-sm" data-id="element-551" />
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm flex gap-2 items-start" data-id="element-552">
          <AlertCircle size={18} className="mt-0.5 shrink-0" data-id="element-553" />
          <span data-id="element-554">{error}</span>
        </div>}

      <div className="bg-blue-50 text-blue-700 p-4 rounded-lg text-sm flex gap-3 items-start" data-id="element-555">
        <CalendarIcon size={18} className="mt-0.5 shrink-0" data-id="element-556" />
        <div data-id="element-557">
          <p className="font-medium" data-id="element-558">Booking Rules</p>
          <p className="opacity-80 mt-1" data-id="element-559">
            Bookings are limited to <strong data-id="element-560">14 days maximum</strong> and can
            only be made up to <strong data-id="element-561">3 months in advance</strong>. Bookings
            can be extended if there is no booking afterwards.
          </p>
        </div>
      </div>
    </div>;
}